import { useEffect, useMemo, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Lock, KeyRound, ArrowRight, ArrowLeft, Heart, Activity, Inbox, Brain,
  Settings, Camera, Pill, Syringe, Dumbbell, Video, Users, MessageCircle,
  Sparkles, Award, Flame, Trophy, ShieldCheck, CheckCircle2, ChevronDown,
  Download, Smartphone, Globe, Feather, Sunrise,
} from "lucide-react";
import { exportPitchPptx, flattenSlideForPptx } from "@/lib/exportPitchPptx";

const PIN = "2353";
const STORAGE_KEY = "still_pitch_unlocked";

// ============ CONTENT MODEL — 10 SLIDES ============
const SLIDES = [
  {
    id: 1,
    kicker: "// 01 · TITLE & VISION",
    tag: "STILL · MOVE DIFFERENTLY. LIVE DIFFERENTLY.",
    title: "Health, movement, memory — <span class=\"text-rose-300\">connected</span>. <span class=\"text-amber-300\">Never punishing.</span>",
    body:
      "STILL is a personal health, fitness, and lifestyle platform built around one belief: your health journey doesn't have to look like anyone else's. It brings the tools people need to understand their health, build better habits, move their bodies, manage their routines, and keep progressing — all inside one connected experience across Web, iOS, and Android.",
    bullets: [
      "One connected ecosystem — nutrition, movement, medication, AI, community, memory",
      "Adaptive fitness that meets you where you are — high, low, or recovery day",
      "AI Coach + Second Brain — long-term context that learns your journey without judging it",
      "Web + iOS + Android full parity — same product, three surfaces",
    ],
  },
  {
    id: 2,
    kicker: "// 02 · THE FIVE PILLARS",
    tag: "PLATFORM ARCHITECTURE",
    title: "Every part of STILL <span class=\"text-rose-300\">feeds the whole</span>.",
    body:
      "Nutrition informs your health picture. Workouts inform your progress. Routines inform your AI Coach. Information lives inside your Second Brain. And your history gives STILL the context to help you move forward.",
    pillars5: [
      { icon: Heart,     tag: "DASHBOARD",  title: "Understand yourself",                desc: "Live KPIs, streaks, nutrition trends, medication status, AI Coach insights — customized to what matters to you.", },
      { icon: Activity,  tag: "PIPELINE",   title: "Build movement & health routines",   desc: "Adaptive workouts, goals, and routines that flex with your energy, time, and experience.", },
      { icon: Inbox,     tag: "INBOX",      title: "Stay on top of what matters",        desc: "Reminders, actionable items, and important information surfaced when you need them.", },
      { icon: Brain,     tag: "BRAIN",      title: "Remember. Learn. Understand.",       desc: "Your searchable Second Brain of notes, reflections, workout history, patterns, and AI conversations.", },
      { icon: Settings,  tag: "SETTINGS",   title: "Make STILL work for you",            desc: "Privacy, preferences, connected services, and AI settings as core product principles.", },
    ],
  },
  {
    id: 3,
    kicker: "// 03 · WEB EXPERIENCE & ONBOARDING",
    tag: "FIRST IMPRESSION",
    title: "Calm. Premium. Modern. <span class=\"text-rose-300\">Inclusive.</span>",
    body:
      "Not a bodybuilding app. Not a medical app. The public STILL experience communicates the philosophy first, the platform second. Visitors discover the philosophy, explore features, learn about the community, and create an account.",
    onboarding: [
      { n: "01", label: "Discover", desc: "Landing page communicates the STILL philosophy and platform vision without gimmicks or hype." },
      { n: "02", label: "Sign Up",  desc: "Simple auth flow — email, secure password, or connect via Apple / Google." },
      { n: "03", label: "Context",  desc: "Preferences, goals, activity level, nutrition style, fitness experience. No forms overload." },
      { n: "04", label: "Move",     desc: "Enter the dashboard already personalized — STILL learns the journey, doesn't judge it." },
    ],
  },
  {
    id: 4,
    kicker: "// 04 · DASHBOARD & NUTRITION",
    tag: "COMMAND CENTER",
    title: "Awareness over obsession. <span class=\"text-rose-300\">Patterns over punishment.</span>",
    body:
      "The Dashboard is your at-a-glance view of the things that matter to you today. The nutrition experience makes tracking easier without turning food into a rulebook.",
    kpis: [
      { label: "Calories",              tone: "rose"   },
      { label: "Macros (P / C / F)",    tone: "amber"  },
      { label: "Fiber & Water",         tone: "rose"   },
      { label: "Activity",              tone: "amber"  },
      { label: "Workout Progress",      tone: "rose"   },
      { label: "Streaks",               tone: "amber"  },
      { label: "Medication Status",     tone: "rose"   },
      { label: "GLP-1 Tracking",        tone: "amber"  },
      { label: "Recovery",              tone: "rose"   },
      { label: "Personal Goals",        tone: "amber"  },
      { label: "AI Coach Insights",     tone: "rose"   },
      { label: "Nutrition Trends",      tone: "amber"  },
    ],
    aiFlow: {
      title: "AI Food Photo Logging",
      subtitle: "Photograph a meal · AI analyzes · user reviews · logs the entry.",
      steps: ["TAKE PHOTO", "REVIEW", "CONFIRM", "LOG"],
      note: "AI estimates are always clearly presented as estimates. The user remains in control of the final entry.",
    },
  },
  {
    id: 5,
    kicker: "// 05 · MEDICATION VAULT & GLP-1",
    tag: "ORGANIZATION · NOT ADVICE",
    title: "A centralized place for meds — <span class=\"text-rose-300\">without pretending to be a doctor</span>.",
    body:
      "The Medication Vault is an organization and tracking tool — not a replacement for a doctor or pharmacist. It lives in the app because health tracking without medication context is incomplete.",
    med: {
      vault: [
        "Add and organize medications",
        "Prescription information storage",
        "Track medication schedules",
        "Full medication history log",
      ],
      ocr: {
        title: "OCR Prescription Scanner",
        steps: ["SCAN", "REVIEW", "CONFIRM", "SAVE"],
        note: "The system never silently assumes extracted information is correct.",
      },
      glp: {
        title: "GLP-1 Tracker",
        features: [
          "Injection date & time history",
          "Medication + dose tracking",
          "Injection-site rotation mapper",
          "Historical records + user notes",
        ],
      },
    },
  },
  {
    id: 6,
    kicker: "// 06 · FITNESS · ADAPTIVE MOVEMENT",
    tag: "MOVEMENT MEETS YOU WHERE YOU ARE",
    title: "Your workout should <span class=\"text-rose-300\">meet you where you are</span> today.",
    body:
      "STILL's fitness experience is built around adaptive movement instead of one-size-fits-all programs. A high-energy day doesn't have to look like a low-energy day. A beginner doesn't need the same program as an athlete. And a recovery day doesn't mean you failed.",
    fitness: {
      selectors: [
        { title: "ENERGY",        options: ["Low", "Moderate", "High"] },
        { title: "FITNESS LEVEL", options: ["Beginner", "Intermediate", "Advanced"] },
        { title: "FOCUS",         options: ["Strength", "Mobility", "Cardio", "Core", "Full Body", "Recovery", "Custom"] },
      ],
      ai: {
        title: "AI Workout Video Generation",
        engine: "Runway ML",
        desc: "STILL generates an appropriate visual workout experience from your selections. Generated workouts are saved to a durable replay library — build a personal collection instead of recreating the same experience every time.",
      },
    },
  },
  {
    id: 7,
    kicker: "// 07 · COMMUNITY & AI COACH",
    tag: "SUPPORT, NOT SCOREBOARDS",
    title: "\"Let's move together.\" — <span class=\"text-rose-300\">not \"who's the fittest here?\"</span>",
    body:
      "Community should feel encouraging. The AI Coach should feel supportive, practical, and transparent about its limitations. Neither should pretend to be a doctor.",
    dual: [
      {
        icon: Users,
        tag: "COMMUNITY WORKOUTS",
        title: "Discover workouts the community generates",
        items: [
          "Explore by focus, difficulty, duration, energy",
          "Trending & new-release rails",
          "Save community workouts to your personal library",
          "Encouraging tone — never leaderboarded",
        ],
      },
      {
        icon: MessageCircle,
        tag: "AI COACH",
        title: "A chat drawer that follows you",
        items: [
          "\"What workout fits my energy today?\"",
          "\"How have my workouts been trending?\"",
          "\"Help me plan tomorrow.\"",
          "\"What have I been struggling with lately?\"",
        ],
      },
    ],
  },
  {
    id: 8,
    kicker: "// 08 · SECOND BRAIN & MOBILE",
    tag: "MEMORY · CONTEXT · PARITY",
    title: "Your journey — <span class=\"text-rose-300\">searchable, portable, yours</span>.",
    body:
      "The Second Brain gives users a place to collect and organize information about their own journey — notes, goals, reflections, patterns, AI conversations, milestones. Over time it becomes a searchable representation of the journey itself. Mobile ships with full parity — camera-first for food, prescriptions, and notes.",
    brainMobile: {
      brain: [
        "Notes, goals, and reflections",
        "Workout & nutrition patterns",
        "AI conversation history",
        "Progress milestones",
      ],
      questions: [
        "What have I learned?",
        "What patterns do I keep seeing?",
        "What was working for me a few months ago?",
      ],
      mobile: [
        { icon: Smartphone, label: "Same 5 tabs on iOS + Android — Dashboard · Pipeline · Inbox · Brain · Settings" },
        { icon: Camera,     label: "Camera-first: food photos, prescription OCR, quick notes" },
        { icon: Sparkles,   label: "Full AI parity — Coach, video generation, Second Brain, recommendations" },
      ],
    },
  },
  {
    id: 9,
    kicker: "// 09 · RED PANDA COMPANION + GAMIFICATION",
    tag: "PERSONALITY · WITHOUT CHILDISHNESS",
    title: "The little reminder that makes the product <span class=\"text-rose-300\">feel alive</span>.",
    body:
      "STILL introduces an optional Red Panda AI companion — a friendly personality layer throughout the mobile experience. Quiet encouragement + personality + companionship. It's not the product; it's the spark. And gamification celebrates progress, not perfection.",
    panda: [
      "Reacts to completed workouts, streaks, and milestones",
      "Notices meals logged, returns after time away",
      "Charming without being childish — quiet encouragement",
      "Fully optional — user-owned personality layer",
    ],
    gamification: {
      systems: [
        { icon: Sparkles, tag: "XP",      title: "Experience Points", desc: "Awarded for meaningful actions across the platform." },
        { icon: Flame,    tag: "STREAKS", title: "Habit Recognition",  desc: "For maintaining consistent habits — with grace on the return." },
        { icon: Award,    tag: "BADGES",  title: "Personal Milestones", desc: "Marks accomplishments and personal progress, not comparisons." },
      ],
      badges: [
        { name: "FIRST STEP",       desc: "Complete your first workout." },
        { name: "STILL SHOWING UP", desc: "Return after taking time away." },
        { name: "IN MOTION",        desc: "Complete a movement milestone." },
        { name: "CONSISTENT",       desc: "Build a sustained habit." },
        { name: "COMEBACK",         desc: "Return after a long break." },
        { name: "STILL GROWING",    desc: "Reach a meaningful personal milestone." },
      ],
    },
  },
  {
    id: 10,
    kicker: "// 10 · THE CORE DIFFERENCE",
    tag: "BRAND PHILOSOPHY & CLOSING",
    title: "Most apps ask, \"How much can you do?\" — STILL asks, <span class=\"text-rose-300\">\"What can you do today?\"</span>",
    body:
      "Most health platforms separate everything into disconnected tools. STILL connects the pieces. Nutrition. Movement. Medication organization. AI. Community. Memory. Progress. Life. The result is a platform that understands health as something bigger than a workout.",
    closing: {
      mantras: [
        "Still here.", "Still becoming.", "Still moving.",
        "Still choosing yourself.", "Still capable.", "Still returning.",
        "Still growing.", "Still tired.", "Still learning.",
        "Still figuring it out.", "Still in motion.",
      ],
      builtFor: [
        "The person starting over",
        "The person trying again",
        "The person rebuilding their health",
        "The parent creating a healthier future for their family",
        "The beginner who doesn't feel like they belong in a traditional gym",
        "The experienced athlete trying to find balance",
        "The person who disappeared for a while and is finally ready to come back",
      ],
      signoff: "STILL doesn't ask people to become someone else. It helps them keep becoming themselves.",
      tagline: "Move differently. Live differently. STILL.",
    },
  },
];

// ============ PIN LOCK ============
function PinLock({ onUnlock }) {
  const [entered, setEntered] = useState(["", "", "", ""]);
  const [error, setError] = useState(false);

  const handleChange = (i, val) => {
    if (val && !/^\d$/.test(val)) return;
    const next = [...entered];
    next[i] = val;
    setEntered(next);
    setError(false);
    if (val && i < 3) document.getElementById(`still-pin-${i + 1}`)?.focus();
    if (next.join("").length === 4) {
      if (next.join("") === PIN) {
        sessionStorage.setItem(STORAGE_KEY, "1");
        setTimeout(onUnlock, 300);
      } else {
        setError(true);
        setTimeout(() => setEntered(["", "", "", ""]), 400);
      }
    }
  };

  const handleKey = (i, e) => {
    if (e.key === "Backspace" && !entered[i] && i > 0) document.getElementById(`still-pin-${i - 1}`)?.focus();
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center relative overflow-hidden" data-testid="still-pin-lock">
      <div className="absolute inset-0 te-grid-bg opacity-20" aria-hidden />
      <motion.div initial={{opacity:0,scale:0.6}} animate={{opacity:1,scale:1}} transition={{duration:2.2}} className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-rose-500/12 blur-[140px]" />
      <motion.div initial={{opacity:0,scale:0.5}} animate={{opacity:1,scale:1}} transition={{duration:2.6,delay:0.3}} className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-amber-500/12 blur-[140px]" />

      <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.8}} className="relative bg-slate-900/60 border border-slate-800/80 backdrop-blur-xl rounded-3xl p-10 max-w-md w-full mx-6 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)]">
        <div className="flex items-center justify-center mb-6">
          <div className="h-16 w-16 rounded-2xl bg-rose-500/10 border border-rose-500/40 flex items-center justify-center">
            <Lock size={26} className="text-rose-300" />
          </div>
        </div>
        <div className="text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-rose-300 mb-3">// CONFIDENTIAL · PIN REQUIRED</div>
          <h1 className="font-display text-3xl font-bold tracking-tighter">STILL · Pitch Vault</h1>
          <p className="mt-3 text-sm text-slate-400 leading-relaxed">
            This deck contains confidential product, brand, and roadmap information. Enter your 4-digit access PIN to unlock.
          </p>
        </div>

        <div className="mt-8 flex items-center justify-center gap-3">
          {entered.map((v, i) => (
            <input
              key={i}
              id={`still-pin-${i}`}
              data-testid={`still-pin-${i}`}
              value={v}
              onChange={(e) => handleChange(i, e.target.value.slice(-1))}
              onKeyDown={(e) => handleKey(i, e)}
              inputMode="numeric"
              maxLength={1}
              type="password"
              autoComplete="off"
              className={`h-16 w-14 text-center font-display text-3xl font-bold rounded-2xl bg-slate-950 border-2 outline-none transition-all ${error ? "border-red-500 animate-pulse" : v ? "border-rose-300 shadow-[0_0_15px_-3px_rgba(244,114,182,0.5)]" : "border-slate-800 focus:border-rose-300/60"}`}
            />
          ))}
        </div>

        {error && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} className="mt-4 text-center font-mono text-[11px] uppercase tracking-[0.22em] text-red-400">
            // ACCESS DENIED · INVALID PIN
          </motion.div>
        )}

        <div className="mt-8 pt-6 border-t border-slate-800/60 flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
          <KeyRound size={11} /> Session-locked · closes with your browser tab
        </div>
      </motion.div>
    </div>
  );
}

// ============ EXECUTIVE SUMMARY ============
function ExecutiveSummary() {
  return (
    <section className="relative overflow-hidden border-b border-slate-800/80" data-testid="still-summary">
      <div className="absolute inset-0 te-grid-bg opacity-25" aria-hidden />
      <motion.div initial={{opacity:0,scale:0.6}} animate={{opacity:1,scale:1}} transition={{duration:2.2}} className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-rose-500/12 blur-[140px]" />
      <motion.div initial={{opacity:0,scale:0.5}} animate={{opacity:1,scale:1}} transition={{duration:2.6,delay:0.3}} className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-amber-500/12 blur-[140px]" />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-20 lg:pt-28 pb-14 lg:pb-20">
        <motion.div initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} transition={{duration:0.6}} className="inline-flex items-center gap-2 border border-rose-300/40 bg-rose-500/[0.06] text-rose-300 px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-[0.22em]">
          <Feather size={12} /> UNLOCKED · STILL · Confidential Product Deck
        </motion.div>

        <motion.h1 initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.9,delay:0.1,ease:[0.22,1,0.36,1]}} className="mt-6 font-display text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.98]">
          STILL — <span className="text-rose-300">move differently.</span> <span className="text-amber-300">live differently.</span>
        </motion.h1>

        <motion.p initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.8,delay:0.25}} className="mt-6 text-base sm:text-lg text-slate-300 max-w-4xl leading-relaxed">
          A personal health, fitness, and lifestyle platform built around one belief: your health journey doesn't have to look like anyone else's. STILL connects <span className="text-rose-300 font-semibold">nutrition</span>, <span className="text-rose-300 font-semibold">adaptive movement</span>, <span className="text-amber-300 font-semibold">medication organization</span>, <span className="text-amber-300 font-semibold">AI Coach</span>, <span className="text-rose-300 font-semibold">community</span>, and a <span className="text-amber-300 font-semibold">Second Brain</span> into one ecosystem across Web, iOS, and Android. STILL isn't about perfection. It's about staying in motion.
        </motion.p>

        {/* TL;DR grid */}
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { k: "// THE THESIS",   t: "\"What can you do today?\"",           d: "Most apps ask how much. STILL asks what fits — energy, time, experience, recovery — no punishment.",         accent: "rose"  },
            { k: "// THE STACK",    t: "5 pillars · one ecosystem",             d: "Dashboard · Pipeline · Inbox · Brain · Settings. Every module feeds context back into the AI Coach.",      accent: "amber" },
            { k: "// THE MOAT",     t: "Adaptive AI + Second Brain",            d: "AI Coach with long-term memory, plus Runway-powered generated workouts and OCR for meals & scripts.",     accent: "rose"  },
            { k: "// THE FEEL",     t: "Calm, premium, human, inclusive",       d: "Not a bodybuilding app. Not a medical app. A Red Panda companion, not a leaderboard — quiet encouragement.", accent: "amber" },
          ].map((c, i) => (
            <motion.div key={i} initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.6,delay:0.3 + i*0.05}} className="bg-slate-900/60 border border-slate-800/80 backdrop-blur-xl rounded-2xl p-5">
              <div className={`font-mono text-[10px] uppercase tracking-[0.22em] ${c.accent === "amber" ? "text-amber-300" : "text-rose-300"}`}>{c.k}</div>
              <h3 className="mt-2 font-display text-lg font-bold">{c.t}</h3>
              <p className="mt-2 text-[13px] text-slate-400 leading-relaxed">{c.d}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap gap-3">
          <a href="#deck" data-testid="still-cta-view-deck" className="inline-flex items-center gap-2 bg-rose-400 text-slate-950 px-6 py-3.5 rounded-2xl font-mono text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-rose-300 hover:scale-[1.02] transition-all shadow-[0_0_30px_-8px_rgba(251,113,133,0.6)]">
            <ArrowRight size={13} /> View Full Deck
          </a>
          <a href="#deck" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" }); }} className="inline-flex items-center gap-2 border border-slate-700 bg-slate-900/60 backdrop-blur-xl text-slate-200 px-6 py-3.5 rounded-2xl font-mono text-[11px] uppercase tracking-[0.22em] hover:bg-slate-800 transition-all">
            <ChevronDown size={13} /> Scroll Through
          </a>
        </div>
      </div>
    </section>
  );
}

// ============ INFOGRAPHIC ============
function Infographic() {
  const pillars = [
    { icon: Heart,    label: "DASHBOARD", desc: "Understand yourself" },
    { icon: Activity, label: "PIPELINE",  desc: "Build routines" },
    { icon: Inbox,    label: "INBOX",     desc: "Stay on top" },
    { icon: Brain,    label: "BRAIN",     desc: "Remember & learn" },
    { icon: Settings, label: "SETTINGS",  desc: "Make STILL yours" },
  ];
  return (
    <section className="relative py-16 lg:py-24 border-b border-slate-800/80" data-testid="still-infographic">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.7}}>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-rose-300 mb-4">// AT-A-GLANCE INFOGRAPHIC</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter max-w-4xl">The five pillars, <span className="text-rose-300">connected</span>.</h2>
        </motion.div>

        {/* 5-pillar visual */}
        <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {pillars.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div key={i} initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.5,delay:i*0.06}} className="bg-slate-900/60 border border-rose-300/30 backdrop-blur-xl rounded-2xl p-5">
                <div className="h-10 w-10 rounded-xl bg-rose-500/10 border border-rose-300/40 flex items-center justify-center"><Icon size={16} className="text-rose-300" /></div>
                <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-rose-300">{p.label}</div>
                <div className="mt-1 font-display text-lg font-bold text-slate-100">{p.desc}</div>
              </motion.div>
            );
          })}
        </div>

        {/* Two-column: what STILL is / what it isn't */}
        <div className="mt-4 grid lg:grid-cols-2 gap-4">
          <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.6}} className="bg-slate-900/60 border border-rose-300/30 backdrop-blur-xl rounded-2xl p-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-rose-300">// STILL IS</div>
            <h3 className="mt-2 font-display text-2xl font-bold tracking-tight">Calm. Human. <span className="text-rose-300">Connected.</span></h3>
            <ul className="mt-5 space-y-2">
              {[
                "One connected ecosystem, not a folder of apps",
                "Adaptive to your energy, time, and experience",
                "Long-term memory that respects your journey",
                "Camera-first mobile — food, scripts, notes",
                "AI Coach that supports without prescribing",
              ].map((x) => (
                <li key={x} className="flex items-start gap-2 text-[13px] text-slate-200 leading-relaxed">
                  <CheckCircle2 size={12} className="text-rose-300 mt-1 shrink-0" /><span>{x}</span>
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.6,delay:0.1}} className="bg-slate-900/60 border border-amber-300/30 backdrop-blur-xl rounded-2xl p-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-300">// STILL IS NOT</div>
            <h3 className="mt-2 font-display text-2xl font-bold tracking-tight">Not a bodybuilding app. <span className="text-amber-300">Not a doctor.</span></h3>
            <ul className="mt-5 space-y-2">
              {[
                "Not a punishment-based fitness tracker",
                "Not a medical decision-maker or diagnostic tool",
                "Not a leaderboard or competitive scoreboard",
                "Not another disconnected single-purpose SaaS",
                "Not judgmental of pauses, restarts, or slow days",
              ].map((x) => (
                <li key={x} className="flex items-start gap-2 text-[13px] text-slate-200 leading-relaxed">
                  <ShieldCheck size={12} className="text-amber-300 mt-1 shrink-0" /><span>{x}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        {/* Brand mantra strip */}
        <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.6,delay:0.1}} className="mt-4 rounded-2xl p-6 bg-gradient-to-br from-rose-500/10 via-slate-900/60 to-amber-500/10 border border-rose-300/40 backdrop-blur-xl">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-rose-300">// BRAND MANTRA</div>
          <div className="mt-3 flex flex-wrap gap-2">
            {["Still here.", "Still becoming.", "Still moving.", "Still choosing yourself.", "Still capable.", "Still returning.", "Still growing.", "Still in motion."].map((m) => (
              <span key={m} className="inline-flex items-center border border-rose-300/40 bg-slate-950/50 text-slate-100 px-3 py-1.5 rounded-full font-display text-sm">
                <Sunrise size={12} className="text-amber-300 mr-2" /> {m}
              </span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ============ SLIDE RENDERERS ============
function SlideBody({ slide }) {
  const heading = (
    <>
      {slide.tag && <div className="inline-flex items-center gap-2 border border-rose-300/40 bg-rose-500/[0.06] text-rose-300 px-2.5 py-1 rounded-full font-mono text-[9px] uppercase tracking-[0.22em] mb-4">{slide.tag}</div>}
      <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-rose-300 mb-3">{slide.kicker}</div>
      <h2 className="font-display text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tighter leading-[1.02]" dangerouslySetInnerHTML={{ __html: slide.title }} />
      <p className="mt-5 text-slate-400 text-base sm:text-lg leading-relaxed max-w-3xl">{slide.body}</p>
    </>
  );

  // 1: bullets
  if (slide.bullets) {
    return (
      <div>{heading}
        <div className="mt-8 grid sm:grid-cols-2 gap-3">
          {slide.bullets.map((b, i) => (
            <div key={i} className="flex items-start gap-3 bg-slate-900/40 border border-rose-300/20 rounded-2xl p-4">
              <CheckCircle2 size={16} className="text-rose-300 mt-0.5 shrink-0" />
              <span className="text-sm text-slate-200 leading-relaxed">{b}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // 2: 5 pillars
  if (slide.pillars5) {
    return (
      <div>{heading}
        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {slide.pillars5.map((p, i) => {
            const Icon = p.icon;
            return (
              <div key={i} className="rounded-2xl p-5 border border-rose-300/30 bg-slate-900/50">
                <div className="h-10 w-10 rounded-xl bg-rose-500/10 border border-rose-300/50 flex items-center justify-center"><Icon size={16} className="text-rose-300" /></div>
                <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-rose-300">{p.tag}</div>
                <h4 className="mt-1 font-display text-lg font-bold text-slate-100">{p.title}</h4>
                <p className="mt-2 text-[13px] text-slate-300 leading-relaxed">{p.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // 3: onboarding
  if (slide.onboarding) {
    return (
      <div>{heading}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-3">
          {slide.onboarding.map((s, i) => (
            <div key={i} className="relative rounded-2xl p-5 border border-rose-300/30 bg-slate-900/50">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-rose-300">STEP {s.n}</div>
              <div className="mt-3 font-display text-xl font-bold text-slate-100">{s.label}</div>
              <div className="mt-2 text-[13px] text-slate-300 leading-relaxed">{s.desc}</div>
              {i < slide.onboarding.length - 1 && <ArrowRight size={16} className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 text-rose-300" />}
            </div>
          ))}
        </div>
        <div className="mt-4 border border-amber-300/30 bg-amber-500/[0.04] rounded-2xl p-4 flex items-center gap-3">
          <Globe size={16} className="text-amber-300 shrink-0" />
          <div className="text-[13px] text-slate-200 leading-relaxed">
            <span className="text-amber-300 font-semibold">Web-first landing.</span> The public site introduces the philosophy, brand, platform, and community — before ever asking for a signup.
          </div>
        </div>
      </div>
    );
  }

  // 4: KPI dashboard + AI food photo
  if (slide.kpis) {
    return (
      <div>{heading}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {slide.kpis.map((k, i) => {
            const rose = k.tone === "rose";
            return (
              <div key={i} className={`rounded-xl p-3.5 border ${rose ? "border-rose-300/30 bg-rose-500/[0.04]" : "border-amber-300/30 bg-amber-500/[0.04]"}`}>
                <div className={`font-mono text-[9px] uppercase tracking-[0.22em] ${rose ? "text-rose-300" : "text-amber-300"}`}>KPI</div>
                <div className="mt-1 font-display text-base font-bold text-slate-100">{k.label}</div>
              </div>
            );
          })}
        </div>
        <div className="mt-4 rounded-2xl p-6 border-2 border-rose-300/40 bg-slate-900/50">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-rose-500/10 border border-rose-300/50 flex items-center justify-center"><Camera size={16} className="text-rose-300" /></div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-rose-300">// AI FOOD PHOTO LOGGING</div>
              <div className="font-display text-lg font-bold text-slate-100">{slide.aiFlow.title}</div>
            </div>
          </div>
          <p className="mt-3 text-[13px] text-slate-300 leading-relaxed">{slide.aiFlow.subtitle}</p>
          <div className="mt-5 flex flex-wrap items-center gap-2">
            {slide.aiFlow.steps.map((s, i) => (
              <div key={s} className="flex items-center gap-2">
                <span className="inline-flex items-center border border-rose-300/40 bg-slate-950/50 text-slate-100 px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-[0.22em] font-bold">{s}</span>
                {i < slide.aiFlow.steps.length - 1 && <ArrowRight size={12} className="text-rose-300" />}
              </div>
            ))}
          </div>
          <div className="mt-4 border border-amber-300/30 bg-amber-500/[0.04] rounded-xl p-3 flex items-start gap-2">
            <ShieldCheck size={14} className="text-amber-300 mt-0.5 shrink-0" />
            <span className="text-[12px] text-slate-300 leading-relaxed">{slide.aiFlow.note}</span>
          </div>
        </div>
      </div>
    );
  }

  // 5: medication
  if (slide.med) {
    const m = slide.med;
    return (
      <div>{heading}
        <div className="mt-8 grid lg:grid-cols-2 gap-3">
          <div className="rounded-2xl p-5 border border-rose-300/30 bg-slate-900/50">
            <div className="h-10 w-10 rounded-xl bg-rose-500/10 border border-rose-300/50 flex items-center justify-center"><Pill size={16} className="text-rose-300" /></div>
            <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-rose-300">// MEDICATION VAULT</div>
            <h4 className="mt-1 font-display text-lg font-bold">Centralize your meds</h4>
            <ul className="mt-3 space-y-2">
              {m.vault.map((v) => <li key={v} className="flex items-start gap-2 text-[13px] text-slate-300"><CheckCircle2 size={12} className="text-rose-300 mt-1 shrink-0" /><span>{v}</span></li>)}
            </ul>
          </div>
          <div className="rounded-2xl p-5 border border-amber-300/30 bg-slate-900/50">
            <div className="h-10 w-10 rounded-xl bg-amber-500/10 border border-amber-300/50 flex items-center justify-center"><Camera size={16} className="text-amber-300" /></div>
            <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-amber-300">// OCR PRESCRIPTION SCANNER</div>
            <h4 className="mt-1 font-display text-lg font-bold">{m.ocr.title}</h4>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {m.ocr.steps.map((s, i) => (
                <div key={s} className="flex items-center gap-2">
                  <span className="inline-flex items-center border border-amber-300/40 bg-slate-950/50 text-slate-100 px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-[0.22em] font-bold">{s}</span>
                  {i < m.ocr.steps.length - 1 && <ArrowRight size={12} className="text-amber-300" />}
                </div>
              ))}
            </div>
            <p className="mt-3 text-[12px] text-slate-400 leading-relaxed italic">{m.ocr.note}</p>
          </div>
        </div>
        <div className="mt-3 rounded-2xl p-5 border-2 border-rose-300/40 bg-slate-900/50">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-rose-500/10 border border-rose-300/50 flex items-center justify-center"><Syringe size={16} className="text-rose-300" /></div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-rose-300">// GLP-1 TRACKER</div>
              <div className="font-display text-lg font-bold">{m.glp.title}</div>
            </div>
          </div>
          <ul className="mt-3 grid md:grid-cols-2 gap-2">
            {m.glp.features.map((f) => <li key={f} className="flex items-start gap-2 text-[13px] text-slate-300"><CheckCircle2 size={12} className="text-rose-300 mt-1 shrink-0" /><span>{f}</span></li>)}
          </ul>
          <div className="mt-4 border border-slate-700/60 bg-slate-950/50 rounded-xl p-3 flex items-start gap-2">
            <ShieldCheck size={14} className="text-amber-300 mt-0.5 shrink-0" />
            <span className="text-[12px] text-slate-300 leading-relaxed">STILL is an organization + tracking tool. It never presents itself as a medical professional and always directs users to their healthcare provider for medical decisions.</span>
          </div>
        </div>
      </div>
    );
  }

  // 6: fitness selectors + Runway AI
  if (slide.fitness) {
    const f = slide.fitness;
    return (
      <div>{heading}
        <div className="mt-8 grid md:grid-cols-3 gap-3">
          {f.selectors.map((s) => (
            <div key={s.title} className="rounded-2xl p-5 border border-rose-300/30 bg-slate-900/50">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-rose-300">{s.title}</div>
              <div className="mt-3 flex flex-wrap gap-2">
                {s.options.map((o) => (
                  <span key={o} className="inline-flex items-center border border-rose-300/40 bg-slate-950/50 text-slate-100 px-2.5 py-1 rounded-full font-mono text-[11px]">{o}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 rounded-2xl p-6 border-2 border-amber-300/40 bg-gradient-to-br from-amber-500/10 via-slate-900/60 to-rose-500/10 backdrop-blur-xl">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-amber-500/10 border border-amber-300/50 flex items-center justify-center"><Video size={16} className="text-amber-300" /></div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-300">// AI WORKOUT VIDEO · POWERED BY {f.ai.engine.toUpperCase()}</div>
              <div className="font-display text-lg font-bold">{f.ai.title}</div>
            </div>
          </div>
          <p className="mt-3 text-[13px] text-slate-300 leading-relaxed">{f.ai.desc}</p>
        </div>
      </div>
    );
  }

  // 7: dual community + coach
  if (slide.dual) {
    return (
      <div>{heading}
        <div className="mt-8 grid lg:grid-cols-2 gap-4">
          {slide.dual.map((d, i) => {
            const Icon = d.icon;
            const isCoach = d.tag === "AI COACH";
            const box = isCoach
              ? "border-amber-300/40 shadow-[0_0_60px_-15px_rgba(251,191,36,0.35)]"
              : "border-rose-300/40 shadow-[0_0_60px_-15px_rgba(244,114,182,0.35)]";
            const chip = isCoach ? "text-amber-300 bg-amber-500/10 border-amber-300/50" : "text-rose-300 bg-rose-500/10 border-rose-300/50";
            return (
              <div key={i} className={`rounded-2xl p-6 border-2 bg-slate-900/50 ${box}`}>
                <div className={`h-10 w-10 rounded-xl border flex items-center justify-center ${chip}`}><Icon size={16} /></div>
                <div className={`mt-3 font-mono text-[10px] uppercase tracking-[0.22em] ${isCoach ? "text-amber-300" : "text-rose-300"}`}>{d.tag}</div>
                <h3 className="mt-1 font-display text-xl font-bold text-slate-100">{d.title}</h3>
                <ul className="mt-4 space-y-2">
                  {d.items.map((x) => (
                    <li key={x} className="flex items-start gap-2 text-[13px] text-slate-300 leading-relaxed">
                      <CheckCircle2 size={12} className={`${isCoach ? "text-amber-300" : "text-rose-300"} mt-1 shrink-0`} /><span>{x}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // 8: second brain + mobile
  if (slide.brainMobile) {
    const b = slide.brainMobile;
    return (
      <div>{heading}
        <div className="mt-8 grid lg:grid-cols-2 gap-4">
          <div className="rounded-2xl p-5 border-2 border-rose-300/40 bg-slate-900/50">
            <div className="h-10 w-10 rounded-xl bg-rose-500/10 border border-rose-300/50 flex items-center justify-center"><Brain size={16} className="text-rose-300" /></div>
            <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-rose-300">// SECOND BRAIN</div>
            <h3 className="mt-1 font-display text-xl font-bold">Long-term memory of your journey</h3>
            <ul className="mt-4 space-y-1.5">
              {b.brain.map((x) => <li key={x} className="flex items-start gap-2 text-[13px] text-slate-300"><CheckCircle2 size={12} className="text-rose-300 mt-1 shrink-0" /><span>{x}</span></li>)}
            </ul>
            <div className="mt-4 border border-rose-300/30 bg-rose-500/[0.04] rounded-xl p-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-rose-300 mb-2">// ASK YOUR BRAIN</div>
              {b.questions.map((q) => <div key={q} className="font-display text-sm text-slate-100 py-0.5">&ldquo;{q}&rdquo;</div>)}
            </div>
          </div>
          <div className="rounded-2xl p-5 border-2 border-amber-300/40 bg-slate-900/50">
            <div className="h-10 w-10 rounded-xl bg-amber-500/10 border border-amber-300/50 flex items-center justify-center"><Smartphone size={16} className="text-amber-300" /></div>
            <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-amber-300">// MOBILE · iOS + ANDROID</div>
            <h3 className="mt-1 font-display text-xl font-bold">Full parity — not a companion</h3>
            <ul className="mt-4 space-y-2.5">
              {b.mobile.map((m) => {
                const Icon = m.icon;
                return (
                  <li key={m.label} className="flex items-start gap-2.5">
                    <Icon size={14} className="text-amber-300 mt-1 shrink-0" />
                    <span className="text-[13px] text-slate-300 leading-relaxed">{m.label}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // 9: red panda + gamification
  if (slide.panda) {
    return (
      <div>{heading}
        <div className="mt-8 grid lg:grid-cols-2 gap-4">
          <div className="rounded-2xl p-6 border-2 border-rose-300/40 bg-gradient-to-br from-rose-500/10 via-slate-900/60 to-amber-500/10">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-rose-300">// RED PANDA COMPANION</div>
            <h3 className="mt-2 font-display text-2xl font-bold">Optional. Charming. Never childish.</h3>
            <ul className="mt-4 space-y-2">
              {slide.panda.map((p) => <li key={p} className="flex items-start gap-2 text-[13px] text-slate-300"><CheckCircle2 size={12} className="text-rose-300 mt-1 shrink-0" /><span>{p}</span></li>)}
            </ul>
          </div>
          <div className="rounded-2xl p-5 border border-amber-300/40 bg-slate-900/50">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-300">// GAMIFICATION SYSTEMS</div>
            <div className="mt-4 space-y-3">
              {slide.gamification.systems.map((s) => {
                const Icon = s.icon;
                return (
                  <div key={s.tag} className="flex items-start gap-3 border border-amber-300/30 bg-slate-950/50 rounded-xl p-3.5">
                    <div className="h-9 w-9 rounded-lg bg-amber-500/10 border border-amber-300/50 flex items-center justify-center shrink-0"><Icon size={14} className="text-amber-300" /></div>
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-300 font-bold">{s.tag}</div>
                      <div className="font-display text-sm font-bold text-slate-100">{s.title}</div>
                      <div className="text-[12px] text-slate-400 leading-relaxed">{s.desc}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Badges strip */}
        <div className="mt-4 rounded-2xl p-5 border border-rose-300/30 bg-slate-900/50">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-rose-300 mb-4 flex items-center gap-2"><Trophy size={12} /> BADGES · Celebrating progress, not perfection</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {slide.gamification.badges.map((b) => (
              <div key={b.name} className="border border-rose-300/25 bg-slate-950/50 rounded-xl p-3">
                <div className="font-display text-sm font-bold text-rose-300">{b.name}</div>
                <div className="mt-0.5 text-[12px] text-slate-400 leading-relaxed">{b.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // 10: closing / brand philosophy
  if (slide.closing) {
    const c = slide.closing;
    return (
      <div>{heading}
        <div className="mt-8 grid lg:grid-cols-2 gap-4">
          <div className="rounded-2xl p-6 border-2 border-rose-300/40 bg-slate-900/50">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-rose-300">// BRAND MANTRA</div>
            <div className="mt-4 flex flex-wrap gap-2">
              {c.mantras.map((m) => (
                <span key={m} className="inline-flex items-center border border-rose-300/40 bg-slate-950/50 text-slate-100 px-3 py-1.5 rounded-full font-display text-sm">
                  {m}
                </span>
              ))}
            </div>
          </div>
          <div className="rounded-2xl p-6 border-2 border-amber-300/40 bg-slate-900/50">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-300">// BUILT FOR</div>
            <ul className="mt-4 space-y-2">
              {c.builtFor.map((b) => <li key={b} className="flex items-start gap-2 text-[13px] text-slate-200 leading-relaxed"><Heart size={12} className="text-amber-300 mt-1 shrink-0" /><span>{b}</span></li>)}
            </ul>
          </div>
        </div>
        <div className="mt-4 rounded-2xl p-6 border border-rose-300/30 bg-gradient-to-br from-rose-500/10 via-slate-900/60 to-amber-500/10 text-center">
          <p className="text-slate-200 italic text-base sm:text-lg leading-relaxed max-w-3xl mx-auto">{c.signoff}</p>
          <div className="mt-4 font-display text-2xl sm:text-3xl font-bold tracking-tight">
            <span className="text-rose-300">Move differently.</span>{" "}
            <span className="text-amber-300">Live differently.</span>{" "}
            <span className="text-slate-100">STILL.</span>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

// ============ DECK ============
function Deck() {
  const [i, setI] = useState(0);
  const [printing, setPrinting] = useState(false);
  const next = useCallback(() => setI((v) => Math.min(SLIDES.length - 1, v + 1)), []);
  const prev = useCallback(() => setI((v) => Math.max(0, v - 1)), []);
  const slide = SLIDES[i];

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") { e.preventDefault(); next(); }
      if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
    };
    window.addEventListener("keydown", onKey);
    const beforePrint = () => setPrinting(true);
    const afterPrint = () => setPrinting(false);
    window.addEventListener("beforeprint", beforePrint);
    window.addEventListener("afterprint", afterPrint);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("beforeprint", beforePrint);
      window.removeEventListener("afterprint", afterPrint);
    };
  }, [next, prev]);

  return (
    <section id="deck" className="relative py-20 lg:py-28 scroll-mt-24" data-testid="still-deck">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.7}}>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-rose-300 mb-4">// FULL PITCH DECK · {SLIDES.length} SLIDES</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter max-w-4xl">The deck.</h2>
          <p className="mt-3 text-slate-400 text-sm">Use ← → arrow keys or click the pager below to navigate. Slide {i + 1} of {SLIDES.length}.</p>
        </motion.div>

        {printing ? (
          <div className="mt-8 space-y-6">
            {SLIDES.map((s) => (
              <div key={s.id} className="bg-slate-900/60 border border-slate-800/80 rounded-3xl p-6 lg:p-12" data-testid={`still-slide-${s.id}`}>
                <SlideBody slide={s} />
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className="mt-8 relative bg-slate-900/60 border border-slate-800/80 backdrop-blur-xl rounded-3xl p-6 lg:p-12 min-h-[560px] overflow-hidden">
              <div className="absolute -top-24 -right-24 w-[400px] h-[400px] rounded-full bg-rose-500/10 blur-[120px]" aria-hidden />
              <div className="absolute -bottom-24 -left-24 w-[400px] h-[400px] rounded-full bg-amber-500/10 blur-[120px]" aria-hidden />
              <AnimatePresence mode="wait">
                <motion.div key={slide.id} initial={{opacity:0, x:30}} animate={{opacity:1, x:0}} exit={{opacity:0, x:-30}} transition={{duration:0.35, ease:[0.22,1,0.36,1]}} className="relative" data-testid={`still-slide-${slide.id}`}>
                  <SlideBody slide={slide} />
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="mt-6 flex items-center justify-between pitch-no-print">
              <button onClick={prev} disabled={i === 0} data-testid="still-deck-prev" className="inline-flex items-center gap-2 border border-slate-700 bg-slate-900/60 backdrop-blur-xl text-slate-200 px-5 py-3 rounded-2xl font-mono text-[11px] uppercase tracking-[0.22em] hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition-all">
                <ArrowLeft size={13} /> Prev
              </button>
              <div className="flex items-center gap-1.5">
                {SLIDES.map((_, idx) => (
                  <button key={idx} onClick={() => setI(idx)} data-testid={`still-deck-dot-${idx}`} aria-label={`Go to slide ${idx + 1}`} className={`h-2 rounded-full transition-all ${idx === i ? "w-8 bg-rose-300" : "w-2 bg-slate-700 hover:bg-slate-500"}`} />
                ))}
              </div>
              <button onClick={next} disabled={i === SLIDES.length - 1} data-testid="still-deck-next" className="inline-flex items-center gap-2 bg-rose-400 text-slate-950 px-5 py-3 rounded-2xl font-mono text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-rose-300 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-[0_0_20px_-6px_rgba(251,113,133,0.6)]">
                Next <ArrowRight size={13} />
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

// ============ MAIN PAGE ============
export default function StillPitch() {
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(STORAGE_KEY) === "1") setUnlocked(true);
  }, []);

  const title = useMemo(() => "STILL · Pitch Vault", []);
  useEffect(() => {
    document.title = title;
    return () => { document.title = "TraceElements · We architect intelligent infrastructure."; };
  }, [title]);

  if (!unlocked) return <PinLock onUnlock={() => setUnlocked(true)} />;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="still-pitch-page">
      <ExecutiveSummary />
      <Infographic />
      <Deck />

      <section className="border-t border-slate-800/80 py-8">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-wrap items-center justify-between gap-4 pitch-no-print">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
            // CONFIDENTIAL · STILL · Prepared by TraceElements Technology Group
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => window.print()} data-testid="still-download-pdf" className="inline-flex items-center gap-2 bg-rose-400 text-slate-950 hover:bg-rose-300 px-4 py-2 rounded-xl font-mono text-[10px] uppercase tracking-[0.22em] font-bold transition-all shadow-[0_0_20px_-6px_rgba(251,113,133,0.6)]">
              <Download size={12} /> Download PDF
            </button>
            <button
              onClick={() => exportPitchPptx({
                vaultName: "STILL",
                title: "STILL · Move Differently. Live Differently.",
                subtitle: "Personal health, fitness, and lifestyle platform — investor deck.",
                brand: "F472B6",
                accent: "FBBF24",
                slides: SLIDES.map((s) => ({ kicker: s.kicker, title: s.title, body: s.body, bullets: flattenSlideForPptx(s) })),
              })}
              data-testid="still-download-pptx"
              className="inline-flex items-center gap-2 border border-rose-300/60 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20 px-4 py-2 rounded-xl font-mono text-[10px] uppercase tracking-[0.22em] font-bold transition-all"
            >
              <Download size={12} /> Download PPT
            </button>
            <button onClick={() => { sessionStorage.removeItem(STORAGE_KEY); window.location.reload(); }} data-testid="still-relock" className="inline-flex items-center gap-2 border border-slate-800 bg-slate-900/60 text-slate-400 hover:text-slate-200 hover:border-slate-700 px-4 py-2 rounded-xl font-mono text-[10px] uppercase tracking-[0.22em] transition-all">
              <Lock size={11} /> Relock Session
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
